import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import { Helmet } from 'react-helmet'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import LinearProgress from '@material-ui/core/LinearProgress'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import CheckIcon from 'components/material-symbol/icons/Check'
import {
  editValidationSchema,
  initForm,
  validateCustomSlugAvailability,
  verifySlug,
} from 'components/coaches-profile/constants'
import DialogAPIMessage from 'components/coaches-profile/profile-components/dialogAPIMessage'
import { submissionHandler } from 'components/coaches-profile/utils'
import { getFirstErrorMessage } from 'pages/pga-coach/bookings/events/form-validation-utils'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import FormSectionContainer from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-section-container'
import EditEventPagination from 'pages/pga-coach/bookings/events/edit/edit-event-form/edit-event-pagination'
import CreateProfileSectionNav, {
  getProfileSections,
  getActiveSectionIndex,
} from '../../new/create-profile-form/create-profile-section-nav'
import AboutYouSection from '../../new/create-profile-form/form-sections/about-you-section'
import ContactLocationSection from '../../new/create-profile-form/form-sections/contact-location-section'
import PersonalMediaSection from '../../new/create-profile-form/form-sections/personal-media-section'
import EditWebAddressSection from '../../new/create-profile-form/form-sections/edit-web-address-section'
import EditFacilitiesSection from '../../new/create-profile-form/form-sections/edit-facilities-section'
import Experience from '../../new/steps/experience'
import SstStep from '../../new/steps/sst'
import {
  getLiveValidationFields,
  getSaveValidationFields,
  handleEditProfileSave,
  nextPublishedSlugAfterSave,
} from './edit-save-validation'

const UPDATE_COACH_PROFILE = gql`
  mutation CoachProfileUpdate(
    $coachProfileId: ID!
    $params: CoachProfileUpdateInput!
  ) {
    CoachProfileUpdate(coachProfileId: $coachProfileId, params: $params) {
      message
      success
    }
  }
`

const EditCoachProfileForm = ({ formFields }) => {
  const classes = useStyles()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const sections = useMemo(() => getProfileSections({ isEdit: true }), [])

  const [openApiResponseDialog, setOpenApiResponseDialog] = useState(false)
  const [apiResponseObject, setApiResponseObject] = useState()
  const [publishedSlug, setPublishedSlug] = useState(
    () => formFields.customSlug || '',
  )
  const [activeSection, setActiveSection] = useState(sections[0].id)
  const [completedSectionIds, setCompletedSectionIds] = useState(
    () => new Set(),
  )
  const completedSectionIdsRef = useRef(completedSectionIds)
  completedSectionIdsRef.current = completedSectionIds

  const activeStepIndex = getActiveSectionIndex(sections, activeSection)
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex === sections.length - 1
  const currentSection = sections[activeStepIndex]

  const defaultValues = useMemo(() => initForm(formFields), [formFields])

  const form = useForm({
    defaultValues,
    shouldUnregister: false,
    resolver: yupResolver(editValidationSchema),
  })

  const { trigger, watch, getValues } = form

  const recordApiResponse = useCallback(
    response => {
      setApiResponseObject(response)
      setPublishedSlug(current =>
        nextPublishedSlugAfterSave({
          success: Boolean(response?.success),
          submittedSlug: getValues('customSlug'),
          publishedSlug: current,
        }),
      )
    },
    [getValues],
  )

  const getStepFields = useCallback(section => section.fields || [], [])

  const getSaveValidationFieldList = useCallback(
    () => getSaveValidationFields(sections),
    [sections],
  )

  const currentSectionFields = useMemo(() => {
    return getLiveValidationFields(getStepFields(currentSection))
  }, [currentSection, getStepFields])

  const watchedSectionValues = watch(currentSectionFields)

  const scrollToFormTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (currentSectionFields.length) {
        await trigger(currentSectionFields, false)
      }
    }, 300)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [currentSectionFields, trigger, watchedSectionValues, activeSection])

  const handleSectionClick = useCallback(
    sectionId => {
      setActiveSection(sectionId)
      scrollToFormTop()
    },
    [scrollToFormTop],
  )

  const markSectionComplete = useCallback(sectionId => {
    setCompletedSectionIds(prev => {
      if (prev.has(sectionId)) {
        return prev
      }

      const next = new Set(prev)
      next.add(sectionId)
      return next
    })
  }, [])

  useEffect(() => {
    if (completedSectionIdsRef.current.size === 0) {
      return undefined
    }

    let cancelled = false
    const timeoutId = setTimeout(async () => {
      const completed = completedSectionIdsRef.current
      const next = new Set()

      for (const section of sections) {
        if (!completed.has(section.id)) {
          continue
        }

        const fields = getLiveValidationFields(section.fields || [])
        const isValid = fields.length ? await trigger(fields, false) : true

        if (!cancelled && isValid) {
          next.add(section.id)
        }
      }

      if (!cancelled) {
        setCompletedSectionIds(prev => {
          if (
            prev.size === next.size &&
            [...prev].every(sectionId => next.has(sectionId))
          ) {
            return prev
          }

          return next
        })
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [sections, trigger, watchedSectionValues])

  const handleCancel = useCallback(() => {
    history.push('/pga-coach/profile')
  }, [history])

  const navigateToSectionWithError = useCallback(
    formErrors => {
      const errorFields = Object.keys(formErrors)
      const sectionWithError = sections.find(section => {
        const fields = getStepFields(section)
        return fields.some(field => errorFields.includes(field))
      })

      if (sectionWithError) {
        setActiveSection(sectionWithError.id)
        scrollToFormTop()
      }
    },
    [getStepFields, scrollToFormTop, sections],
  )

  const showFirstValidationError = useCallback(
    formErrors => {
      const errorMessage = getFirstErrorMessage(formErrors)

      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setActiveSection(sections[activeStepIndex - 1].id)
      scrollToFormTop()
    }
  }, [activeStepIndex, isFirstStep, scrollToFormTop, sections])

  const handleNext = useCallback(async () => {
    if (isLastStep) return

    const fields = getStepFields(currentSection)
    const isValid = fields.length ? await trigger(fields) : true

    if (isValid && fields.includes('customSlug')) {
      const availabilityError = await validateCustomSlugAvailability(
        form.getValues('customSlug'),
        verifySlug,
        { currentSlug: publishedSlug },
      )

      if (availabilityError) {
        form.setError('customSlug', {
          type: 'manual',
          message: availabilityError,
        })
        enqueueSnackbar(availabilityError, { variant: 'error' })
        return
      }
    }

    if (isValid) {
      markSectionComplete(currentSection.id)
      setActiveSection(sections[activeStepIndex + 1].id)
      scrollToFormTop()
      return
    }

    const formErrors = form.formState.errors
    navigateToSectionWithError(formErrors)
    showFirstValidationError(formErrors)
  }, [
    activeStepIndex,
    currentSection,
    enqueueSnackbar,
    form,
    publishedSlug,
    getStepFields,
    isLastStep,
    markSectionComplete,
    navigateToSectionWithError,
    scrollToFormTop,
    sections,
    showFirstValidationError,
    trigger,
  ])

  const [updateProfile, { loading }] = useMutation(UPDATE_COACH_PROFILE)

  const onSubmit = values => {
    submissionHandler(
      values,
      false,
      updateProfile,
      formFields?.coachProfileId,
      recordApiResponse,
      setOpenApiResponseDialog,
      { ...formFields, customSlug: publishedSlug },
    )
  }

  const handleSaveClick = async e => {
    e.preventDefault()

    await handleEditProfileSave({
      trigger,
      getSaveValidationFields: getSaveValidationFieldList,
      getValues,
      onSubmit: async values => {
        const availabilityError = await validateCustomSlugAvailability(
          values.customSlug,
          verifySlug,
          { currentSlug: publishedSlug },
        )

        if (availabilityError) {
          form.setError('customSlug', {
            type: 'manual',
            message: availabilityError,
          })
          setActiveSection('profile-section-web-address')
          scrollToFormTop()
          enqueueSnackbar(availabilityError, { variant: 'error' })
          return
        }

        onSubmit(values)
      },
      onInvalid: () => {
        const formErrors = form.formState.errors
        navigateToSectionWithError(formErrors)
        showFirstValidationError(formErrors)
      },
    })
  }

  const handleDialogClose = () => setOpenApiResponseDialog(false)

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile-section-about':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <AboutYouSection memberProfile={formFields} />
          </FormSectionContainer>
        )
      case 'profile-section-contact':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <ContactLocationSection />
          </FormSectionContainer>
        )
      case 'profile-section-personal':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <PersonalMediaSection includeVideoIntroduction />
          </FormSectionContainer>
        )
      case 'profile-section-web-address':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <EditWebAddressSection customSlug={publishedSlug} />
          </FormSectionContainer>
        )
      case 'profile-section-facilities':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <EditFacilitiesSection
              formFields={formFields}
              onSubmit={onSubmit}
            />
          </FormSectionContainer>
        )
      case 'profile-section-experience':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <Experience hideTitle />
          </FormSectionContainer>
        )
      case 'profile-section-services':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <SstStep hideTitle />
          </FormSectionContainer>
        )
      default:
        return null
    }
  }

  return (
    <>
      <Helmet>
        <script
          src="https://widget.cloudinary.com/v2.0/global/all.js"
          type="text/javascript"
        />
      </Helmet>
      <Box>
        <FormProvider {...form}>
          <form onSubmit={handleSaveClick}>
            <Box className={classes.actionBar}>
              <Button
                type="button"
                className={classes.cancelAction}
                startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.saveAction}
                disabled={loading}
                startIcon={<CheckIcon fontSize="small" />}
              >
                Save Profile
              </Button>
            </Box>

            {loading ? (
              <LinearProgress
                color="primary"
                className={classes.loadingStyles}
              />
            ) : null}
            <Container maxWidth="lg">
              <Box pt={4} pb={3}>
                <Box className={classes.layoutGrid}>
                  <Box className={classes.navColumn}>
                    <CreateProfileSectionNav
                      sections={sections}
                      activeSection={activeSection}
                      completedSectionIds={completedSectionIds}
                      onSectionClick={handleSectionClick}
                    />
                  </Box>

                  <Box className={classes.formColumn}>
                    {renderActiveSection()}
                    <EditEventPagination
                      isFirstStep={isFirstStep}
                      isLastStep={isLastStep}
                      onPrevious={handlePrevious}
                      onNext={handleNext}
                    />
                  </Box>
                </Box>
              </Box>
            </Container>
          </form>
        </FormProvider>
        <DialogAPIMessage
          message={apiResponseObject && apiResponseObject.message}
          success={apiResponseObject && apiResponseObject.success}
          open={openApiResponseDialog}
          handleClose={handleDialogClose}
          isCreate={false}
          customSlug={getValues('customSlug') || publishedSlug}
        />
      </Box>
    </>
  )
}

export default EditCoachProfileForm
